import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Modal, ProgressBar } from 'react-bootstrap';

import { useAppDispatch } from '../../store/hooks';
import ButtonLoader from '../../pages/ButtonLoader';
import { uploadDocuments } from '../../store/features/dashboard';
import { getValue, renderErrorToast } from '../../utility/helpers';
import { closeButton, crossDarkIcon, DocxBigIcon, fileInputIcon, pdfBigIcon, proceedArrow } from '../../assets/images/images';

const allowedExtensions = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];

const UploadDocuments = ({
    userId,
    showUploadDocModal,
    setShowUploadDocModal,
}: any) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [urlInput, setUrlInput] = useState('');
    const [uploading, setUploading] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [filePreview, setFilePreview] = useState<string | null>(null); // State to manage file preview URL

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (urlInput) {
            renderErrorToast('Please clear the URL field before uploading a file.');
            event.target.value = ''; // Clear the selected file input
            return;
        }

        const file = event.target.files?.[0];

        // Ensure 'file' is defined before proceeding
        if (!file) {
            return;
        }

        // Check allowed file extensions
        if (allowedExtensions.includes(file.type)) {
            setSelectedFile(file);
            setFilePreview(URL.createObjectURL(file));
        } else {
            renderErrorToast('Invalid file format. Please use pdf or word.');
        }

        // Clear the file input after processing
        event.target.value = '';
    };

    const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUrlInput(event.target.value);
        setSelectedFile(null); // Clear file selection when entering a URL
        setFilePreview(null); // Clear preview when entering a URL
    };

    const prepareFormData = async (fileOrUrl: File | string, payloadType: '1' | '2') => {
        const formData = new FormData();
        if (payloadType === '1' && selectedFile instanceof File) {
            formData.append('file_name', selectedFile.name);
            formData.append('file', selectedFile);
        } else if (payloadType === '2' && typeof fileOrUrl === 'string') {
            formData.append('file_url', fileOrUrl);
        }
        formData.append('payload_type', payloadType);
        formData.append('user', (userId || getValue('userId')));

        try {
            const res = await dispatch(uploadDocuments(formData)).unwrap();
            setUploading(false);
            setShowLoader(false);
            setShowUploadDocModal(false);
            navigate(`/chat?conversationId=${res?.data?.conversation_id?.id}`);
        } catch (error) {
            console.info('Error', error);
            setUploading(false);
            setShowLoader(false);
        }
    };

    // Handle submission based on URL or file input
    const handleSubmit = () => {
        setShowLoader(true);
        setUploading(false); // Start uploading state when proceed is clicked
        if (urlInput) {
            prepareFormData(urlInput, '2'); // If URL is entered, prepare form data with URL
        } else if (selectedFile) {
            prepareFormData(selectedFile, '1'); // If a file is selected, prepare form data with file
        }
    };

    const getFileNameAndExtension = (fileName: any, isName: any) => {
        const fileParts = fileName?.split('.');
        if (isName) {
            return fileParts.slice(0, -1).join('.');
        } else {
            return fileParts.length > 1 ? `.${fileParts.pop().toLowerCase()}` : '';
        }
    };

    const [imageSrc, setImageSrc] = useState(closeButton); // Default image

  useEffect(() => {
    const updateImageSource = () => {
      // Check if the body has the 'theme-dark' class
      if (document.body.classList.contains('theme-dark')) {
        setImageSrc(crossDarkIcon); // Change to dark theme image
      } else {
        setImageSrc(closeButton); // Change back to the default image
      }
    };

    // Run the function once on mount
    updateImageSource();

    return () => {
      // If you set up any listeners, clean them up here
    };
  }, []);

    return (
        <>
            <Modal
                show={showUploadDocModal}
                dialogClassName="modal-600 top-right-modal modal-dialog-centered"
                className="upload-document-modal"
            >
                <Modal.Body>
                    <h4 className="modal-head modal-head-medium">
                        Upload existing job description for a head start.
                    </h4>
                    {!filePreview && (
                        <div className="upload-document-main">
                            <Form.Group controlId="formFile" className="form-group">
                                <Form.Control
                                    type="file"
                                    className="custom-file-input"
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx" // Allow only PDF, DOC, and DOCX files
                                />
                                <div className="file-input-inner">
                                    <img src={fileInputIcon} alt="file input" className="file-input-img" />
                                    <span className="drag-drop-des">
                                        Drag & Drop or select a file from your system to upload.
                                    </span>
                                    <Button className="add-btn">+Add</Button>
                                </div>

                                {/* this code for loader state */}
                                {uploading && (
                                    <div className="file-input-inner loading-file-wrap">
                                        <span className="loading-text">uploading...</span>
                                        <div className="progress-bar-wrap">
                                            <ProgressBar now={60} />
                                            <img src={closeButton} alt="button" className="cross-btn" />
                                        </div>
                                    </div>
                                )}
                            </Form.Group>
                        </div>
                    )}

                    {/* this code for uploaded file state */}
                    {filePreview && (
                        <div className="upload-document-main uploaded-document-wrap">
                            <div className="uploaded-document-modal-wrap">
                                {selectedFile?.name.endsWith('.pdf') ? (
                                    <img src={pdfBigIcon} alt="PDF document" className="uploaded-document" />
                                ) : (
                                    <img src={DocxBigIcon} alt="DOCX document" className="uploaded-document" />
                                )}

                                <div className="uploaded-document-name">
                                    <span className='uploaded-document-span'>{getFileNameAndExtension(selectedFile?.name, true)}</span>
                                    <span className='uploaded-document-pdf'>{getFileNameAndExtension(selectedFile?.name, false)}</span>
                                </div>
                            </div>
                            <img
                                src={imageSrc}
                                alt="close button"
                                className="cross-btn"
                                onClick={() => {
                                    setSelectedFile(null);
                                    setFilePreview(null);
                                }}
                            />
                        </div>
                    )}

                    <div className="url-wrap">
                        <Form.Group className="form-group" controlId="formBasicUrl">
                            <Form.Control
                                type="text"
                                placeholder="Or Enter URL"
                                value={urlInput}
                                onChange={handleUrlChange}
                                disabled={!!selectedFile} // Disable URL input if a file is selected
                            />
                        </Form.Group>
                    </div>

                    <div className="modal-footer upload-document-modal-actions">
                        <Button
                            className="learn-btn scale upload-document-modal-action-btn"
                            onClick={handleSubmit}
                            disabled={uploading || (!selectedFile && !urlInput)}
                        >
                            {showLoader ? <ButtonLoader /> : 'Proceed'} <img src={proceedArrow} alt="arrow" />
                        </Button>
                        <Button
                            className="upload-document-modal-cancel-btn"
                            onClick={() => setShowUploadDocModal(false)}
                        >
                            Cancel
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default UploadDocuments;
